<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">

    <!-- Adjustment Form Card -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] print-hide">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center text-left">
        <h2 class="text-base font-bold text-[#0d4169]">Stock Adjustment (ADJ)</h2>
        <span v-if="loading" class="text-xs text-slate-400 animate-pulse">Loading data...</span>
      </div>
      <form @submit.prevent="processAdj" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2 text-left">

          <!-- ADJ Number (display-only) -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">ADJ Number</label>
            <input
              :value="adjNumberDisplay || '-'"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            />
          </div>

          <!-- Transaction Date -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <input v-model="form.date" type="date"
              :max="todayDate"
              :min="todayDate"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required />
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
            <select v-model="form.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <!-- Target Item -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Target Item</label>
            <select v-model="form.selectedKey"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" :disabled="!form.categoryId" required>
              <option :value="null" disabled>Select Item</option>
              <option 
                v-for="opt in filteredItems" 
                :key="opt.key" 
                :value="opt.key"
                :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
              >
                {{ opt.itemName }} ({{ opt.unit || '-' }})
              </option>
            </select>
          </div>

          <!-- Plus(+): original grid layout -->
          <template v-if="!isMinusMode">
            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Current Balance</label>
              <input :value="selectedItemBalance === '-' ? '-' : formatBalanceQty(selectedItemBalance)" type="text"
                class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed" readonly>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Adjustment Type</label>
              <select v-model="form.type"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
                <option value="Addition">Plus(+)</option>
                <option value="Subtraction">Minus(-)</option>
              </select>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Quantity</label>
              <input v-model.number="form.quantity" type="number" min="1" step="any"
                inputmode="decimal" placeholder="0"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">
                Volume
                <span class="text-red-600" v-if="volumeUnitLabel">({{ volumeUnitLabel }})</span>
              </label>
              <input v-model.number="form.volume" type="number" min="0" step="0.01" placeholder=""
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Expiry Date</label>
              <input
                v-model="form.expiryDate"
                type="date"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
              />
            </div>

            <div class="flex flex-col gap-1 col-span-1 md:col-span-2 lg:col-span-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Remark / Reason</label>
              <input v-model="form.remark" type="text" placeholder="e.g. Correction of typo..."
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
            </div>
          </template>

          <!-- Minus(-): Adjustment Type + Current Balance + Remark in one row -->
          <div
            v-else
            class="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-5 flex flex-col lg:flex-row gap-4 items-stretch"
          >
            <div class="flex flex-col gap-1 w-full lg:w-[8.25rem] lg:max-w-[8.25rem] lg:shrink-0">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Adjustment Type</label>
              <select v-model="form.type"
                class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
                <option value="Addition">Plus(+)</option>
                <option value="Subtraction">Minus(-)</option>
              </select>
            </div>

            <div class="flex-[1.75] min-w-0 flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">
                Current Balance
                <span v-if="balanceLoading" class="ml-2 text-slate-400 animate-pulse text-xs font-normal">Loading...</span>
              </label>

              <div v-if="!form.selectedKey" class="text-xs text-slate-400 italic px-2 py-1.5 border border-[#cbd5e1] rounded-md min-h-[34px] flex items-center">
                Select an item to see its current stock balance.
              </div>

              <div v-else-if="balanceLoading" class="text-xs text-slate-400 italic px-2 py-1.5 border border-[#cbd5e1] rounded-md min-h-[34px] flex items-center">
                Loading stock balance...
              </div>

              <div v-else class="minus-balance-table-wrap border border-[#cbd5e1] rounded-md">
                <table class="minus-balance-table w-full">
                  <thead>
                    <tr class="bg-slate-50">
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-2 py-1.5">Balance</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-2 py-1.5">Unit Price</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-2 py-1.5">Volume</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-2 py-1.5">Expiry Date</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-2 py-1.5 whitespace-nowrap">Qty to Adjust</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="stockBalances.length === 0">
                      <td colspan="5" class="px-3 py-1.5 text-xs text-amber-600 italic">
                        No stock available for this item.
                      </td>
                    </tr>
                    <tr
                      v-else
                      v-for="(row, idx) in stockBalances"
                      :key="idx"
                      class="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td class="px-2 py-1.5 text-xs font-semibold whitespace-nowrap" :class="row.balance <= 0 ? 'text-red-500' : 'text-emerald-600'">
                        {{ formatBalanceQty(row.balance) }} {{ selectedItemUnit }}
                      </td>
                      <td class="px-2 py-1.5 text-xs text-slate-700 whitespace-nowrap">
                        {{ Number(row.unitPrice || 0).toFixed(2) }}
                      </td>
                      <td class="px-2 py-1.5 text-xs text-slate-700 whitespace-nowrap">
                        {{ row.volume ?? '-' }}
                      </td>
                      <td class="px-2 py-1.5 text-xs text-slate-700 whitespace-nowrap">
                        {{ row.expiryDate || '-' }}
                      </td>
                      <td class="px-2 py-1.5 min-w-[6.75rem]">
                        <input
                          v-model.number="row.adjustQty"
                          type="number"
                          min="0"
                          step="any"
                          inputmode="decimal"
                          :max="row.balance"
                          placeholder="0"
                          :disabled="row.balance <= 0"
                          class="w-full min-w-[5.5rem] max-w-[7rem] px-2 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs disabled:opacity-40 box-border"
                        >
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="flex flex-col gap-1 w-full lg:w-[10.5rem] lg:max-w-[10.5rem] lg:shrink-0">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Remark / Reason</label>
              <input v-model="form.remark" type="text" placeholder="e.g. Correction of typo..."
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
            </div>
          </div>

        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-3 text-left">
          <button v-if="isEditing" type="button" @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            {{ isEditing ? 'Update Item' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Table Section -->
    <div v-if="adjList.length > 0" class="flex flex-col gap-4">

      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <!-- Print Header (Hidden unless printing) -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">Stock Adjustment (ADJ)</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">ADJ No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Balance</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Type</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Expiry Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(adj, index) in adjList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ index + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ adj.adjNo }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ adj.date }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ getCategoryName(adj.categoryId) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] font-semibold" :style="adj.itemType === 'product' ? 'color:#16a34a;' : 'color:#0d4169;'">
                {{ getDisplayName(adj) }}
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ formatBalanceQty(adj.current_balance) }}</td>
              <td class="px-2 py-1.5 text-[0.7rem]">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold" :class="adj.type === 'Addition' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-rose-100 text-rose-700 border border-rose-200'">
                  {{ adj.type === 'Addition' ? 'Plus(+)' : 'Minus(-)' }}
                </span>
              </td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ adj.quantity }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ adj.volume ?? '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ adj.expiryDate || '-' }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ adj.remark || '-' }}</td>
              <td class="px-2 py-1.5 print-hide">
                <div class="flex items-center gap-1">
                  <button @click="editItem(index)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button @click="deleteItem(index)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                   Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
          <div class="flex flex-col gap-1.5 pl-8">
            <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
              <span class="font-black text-[#0d4169]">Total Items:</span>
              <span class="font-black text-base text-[#0d4169]">{{ totalItems }}</span>
            </div>
          </div>

          <div class="mt-16 flex justify-between px-10 text-sm">
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Prepared By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Checked By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Authorized By</strong>
            </div>
          </div>
        </div>
      </div>

       <!-- Summary + Save Button (Below Table) -->
      <div class="flex justify-between items-center px-2 print-hide">
        <div class="text-left">
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Items</p>
          <p class="text-sm font-black text-[#0d4169]">{{ totalItems }}</p>
        </div>
        <button @click="saveToDatabase" :disabled="isSaving"
          class="min-w-[140px] px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 relative flex items-center justify-center">
          <div
  v-if="isSaving"
  class="absolute h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
         <span :class="isSaving ? 'invisible' : 'visible'">{{'Save Adjustments' }}</span> 
        </button>
      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

// Dropdowns
const categories = ref([])
const registeredItems = ref([])
const products = ref([])

// State
const loading = ref(false)
const isSaving = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)

// Display-only ADJ number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const adjNumberDisplay = ref('')

// Context
const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const selectableCategories = computed(() => categories.value)

const todayDate = new Date().toISOString().split('T')[0]

// Form
const getInitialForm = () => ({
  adjNo: '',
  date: new Date().toISOString().slice(0, 10),
  categoryId: null,
  selectedKey: null, // item:ID or product:ID
  itemType: 'item',
  itemId: null,
  productId: null,
  current_balance: 0,
  type: 'Addition',
  quantity: 0,
  volume: "",
  expiryDate: '',
  remark: '',
})
const form = ref(getInitialForm())

const totalItems = computed(() => adjList.value.length)

// Filter - Merge items and products
const filteredItems = computed(() => {
  if (!form.value.categoryId) return []
  const catId = Number(form.value.categoryId)

  const items = registeredItems.value
    .filter(item => Number(item.categoryId) === catId)
    .map(i => ({
      key: `item:${i.id}`,
      type: 'item',
      id: i.id,
      itemName: i.itemName,
      unit: i.unit,
      unitProduction: i.unitProduction,
      item: i
    }))

  const prods = products.value
    .filter(p => {
      const pCat = p.categoryId ?? p.category_id ?? p.category?.id ?? null
      // If no category assigned, show it regardless of category selection (matching grvPage)
      return pCat == null ? true : Number(pCat) === catId
    })
    .map(p => ({
      key: `product:${p.id}`,
      type: 'product',
      id: p.id,
      itemName: p.name,
      unit: p.uom ?? p.unit ?? '',
      unitProduction: p.uom ?? p.unit ?? '',
      item: p
    }))

  return [...items, ...prods].sort((a, b) =>
    String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
  )
})

const selectedItem = computed(() => {
  const key = form.value.selectedKey
  if (!key) return null
  return filteredItems.value.find(f => f.key === key) || null
})

const isMinusMode = computed(() => form.value.type === 'Subtraction')

const selectedItemUnit = computed(() => {
  const sel = selectedItem.value
  if (!sel) return ''
  return sel.unit || sel.unitProduction || ''
})

const stockBalances = ref([])
const balanceLoading = ref(false)
const suppressBalanceWatch = ref(false)

// Automatically find the current balance of the selected item/product
const selectedItemBalance = computed(() => {
  const key = form.value.selectedKey
  if (!key) return '-'
  return balanceMap.value.get(key) ?? 0
})

// Volume unit label: M.L. for Liter products, Gram for K.G. products, unitProduction for items
const volumeUnitLabel = computed(() => {
  const sel = selectedItem.value
  if (!sel) return null
  if (sel.type === 'product') {
    const uom = (sel.unit || '').toLowerCase().replace(/[^a-z0-9]/g, '')
    if (uom === 'l' || uom === 'liter' || uom === 'liters' || uom.startsWith('liter')) return 'M.L.'
    if (uom === 'kg' || uom === 'kilogram') return 'Gram'
    return sel.unit || null
  }
  return sel.unitProduction || null
})

// Temp list
const adjList = ref([])
// Reactive map: key ('item:ID' or 'product:ID') -> current_balance
const balanceMap = ref(new Map())

function makeBalanceKey(unitPrice, volume, expiryDate) {
  const norm = (n) => Math.round(Number(n ?? 0) * 10000) / 10000
  const exp = String(expiryDate || '').trim()
  return `${norm(unitPrice)}|${norm(volume)}|${exp}`
}

function formatBalanceQty(value) {
  const n = typeof value === 'number' ? value : parseFloat(value ?? 0)
  if (!Number.isFinite(n)) return '0.000'
  return (Math.round(n * 1000) / 1000).toFixed(3)
}

function sortBalanceRowsByExpiry(rows) {
  const toDateMs = (value) => {
    const raw = String(value || '').trim()
    if (!raw) return Number.POSITIVE_INFINITY
    const ms = new Date(raw).getTime()
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
  }

  return [...rows].sort((a, b) => {
    const byExpiry = toDateMs(a?.expiryDate) - toDateMs(b?.expiryDate)
    if (byExpiry !== 0) return byExpiry
    const byPrice = Number(a?.unitPrice || 0) - Number(b?.unitPrice || 0)
    if (byPrice !== 0) return byPrice
    return Number(a?.volume || 0) - Number(b?.volume || 0)
  })
}

function getReservedQtyByPriceVolume(type, selectedId, excludeIndex = -1) {
  const reserved = new Map()
  const isSameTarget = (line) => {
    if (type === 'product') return Number(line?.productId) === Number(selectedId)
    return Number(line?.itemId) === Number(selectedId)
  }

  adjList.value.forEach((line, idx) => {
    if (idx === excludeIndex) return
    if (line?.type !== 'Subtraction') return
    if (!isSameTarget(line)) return

    const key = makeBalanceKey(line.unitPrice, line.volume, line.expiryDate)
    const qty = Number(line.quantity || 0)
    if (!Number.isFinite(qty) || qty <= 0) return
    reserved.set(key, (reserved.get(key) || 0) + qty)
  })

  return reserved
}

async function loadStockBalancesForSelection(key, options = {}) {
  if (!key || !isMinusMode.value) {
    stockBalances.value = []
    return
  }

  const { presetRecord = null } = options
  const [type, idStr] = String(key).split(':')
  const id = Number(idStr)
  if (!Number.isFinite(id)) {
    stockBalances.value = []
    return
  }

  balanceLoading.value = true
  try {
    const endpoint =
      type === 'product'
        ? `/products/${id}/stock-balance-with-expiry`
        : `/items/${id}/stock-balance-with-expiry`
    const res = await apiFetch(endpoint)
    if (!res.ok) {
      stockBalances.value = []
      return
    }
    const data = await res.json()
    const reservedQtyByKey = getReservedQtyByPriceVolume(type, id, editIndex.value)

    let rows = sortBalanceRowsByExpiry((data.data || [])
      .map((row) => {
        const balanceKey = makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
        const reservedQty = Number(reservedQtyByKey.get(balanceKey) || 0)
        const currentBalance = Number(row.balance || 0)
        const adjustedBalance = Math.max(0, currentBalance - reservedQty)
        return { ...row, balance: adjustedBalance, adjustQty: null }
      })
      .filter((row) => Number(row.balance) > 0))

    if (presetRecord) {
      const presetKey = makeBalanceKey(
        presetRecord.unitPrice,
        presetRecord.volume,
        presetRecord.expiryDate
      )
      const presetQty = Number(presetRecord.quantity || 0)
      rows = rows.map((row) => {
        const rowKey = makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
        if (rowKey !== presetKey) return row
        return { ...row, adjustQty: presetQty > 0 ? presetQty : null }
      })
      const hasPresetRow = rows.some(
        (row) => makeBalanceKey(row.unitPrice, row.volume, row.expiryDate) === presetKey
      )
      if (!hasPresetRow && presetQty > 0) {
        rows.unshift({
          balance: presetQty,
          unitPrice: Number(presetRecord.unitPrice || 0),
          volume: presetRecord.volume ?? 0,
          expiryDate: presetRecord.expiryDate || '',
          adjustQty: presetQty
        })
      }
    }

    stockBalances.value = rows
  } catch (err) {
    console.error('Failed to load stock balance:', err)
    stockBalances.value = []
  } finally {
    balanceLoading.value = false
  }
}

function normalizeUnit(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.]/g, '')
}

function isLiterOrKgUnit(unit) {
  const u = normalizeUnit(unit)
  return (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'kilogram' ||
    u === 'k.g' ||
    u === 'k.g.'
  )
}

function computeDerivedUnitPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice ?? 0)
  const volume = Number(recentVolume ?? 0)
  if (!(Number.isFinite(price) && Number.isFinite(volume) && volume > 0)) return 0
  return isLiterOrKgUnit(unit)
    ? (price / volume) * 1000
    : (price / volume)
}

function normalizeRecentSnapshotFields(data) {
  if (!data || typeof data !== 'object') return {}
  const recent_unit_price =
    data.recent_unit_price ??
    data.recentUnitPrice ??
    data.recent_price ??
    data.recentPrice ??
    null
  const recent_volume =
    data.recent_volume ??
    data.recentVolume ??
    data.recent_vol ??
    data.recentVol ??
    null
  return { recent_unit_price, recent_volume }
}

async function ensureRecentSnapshotLoadedForSelection(key) {
  if (!key) return
  const [type, idStr] = String(key).split(':')
  const id = Number(idStr)
  if (!Number.isFinite(id)) return

  try {
    const endpoint = type === 'product' ? `/products/${id}` : `/items/${id}`
    const res = await apiFetch(endpoint)
    if (!res.ok) return
    const payload = await res.json().catch(() => null)
    const data = payload?.data ?? payload
    if (!data) return
    const normalized = normalizeRecentSnapshotFields(data)

    if (type === 'product') {
      const idx = products.value.findIndex(p => Number(p.id) === id)
      if (idx >= 0) products.value[idx] = { ...products.value[idx], ...data, ...normalized }
    } else {
      const idx = registeredItems.value.findIndex(i => Number(i.id) === id)
      if (idx >= 0) registeredItems.value[idx] = { ...registeredItems.value[idx], ...data, ...normalized }
    }
  } catch (err) {
    console.error('Failed to load recent price/volume snapshot:', err)
  }
}

// Fetch
async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [catRes, itemRes, prodRes] = await Promise.all([
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/products?${query}&activeOnly=true`)
    ])
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
   categories.value.sort((a, b) => a.name.localeCompare(b.name))

    if (itemRes.ok) registeredItems.value = await itemRes.json()
    if (prodRes.ok) products.value = await prodRes.json()

    await loadCurrentBalances()
  } catch (err) { console.error(err) }
  finally { loading.value = false }
}

async function loadCurrentBalances() {
  const allItems = registeredItems.value || []
  const allProds = products.value || []
  const newMap = new Map()

  // Items
  await Promise.all(
    allItems.map(async (item) => {
      try {
        const res = await apiFetch(`/items/${item.id}/stock-balance`)
        if (!res.ok) { newMap.set(`item:${item.id}`, 0); return }
        const data = await res.json()
        const rows = data.data || []
        const totalBalance = rows.reduce((sum, r) => sum + (Number(r.balance || 0)), 0)
        newMap.set(`item:${item.id}`, Math.round(totalBalance * 1000) / 1000)
      } catch (err) { console.error('Item balance fail', item.id, err); newMap.set(`item:${item.id}`, 0) }
    })
  )

  // Products
  await Promise.all(
    allProds.map(async (prod) => {
      try {
        const res = await apiFetch(`/products/${prod.id}/stock-balance`)
        if (!res.ok) { newMap.set(`product:${prod.id}`, prod.quantity || 0); return }
        const data = await res.json()
        const rows = data.data || []
        const totalBalance = rows.reduce((sum, r) => sum + (Number(r.balance || 0)), 0)
        newMap.set(`product:${prod.id}`, Math.round(totalBalance * 1000) / 1000)
      } catch (err) {
        console.error('Product balance fail', prod.id, err)
        newMap.set(`product:${prod.id}`, prod.quantity || 0)
      }
    })
  )

  // Assign to the reactive ref — this triggers computed re-evaluation
  balanceMap.value = newMap
}

watch(
  () => form.value.selectedKey,
  async (key) => {
    await ensureRecentSnapshotLoadedForSelection(key)
    if (suppressBalanceWatch.value) return
    if (isMinusMode.value && key) {
      await loadStockBalancesForSelection(key)
    } else if (!isMinusMode.value) {
      stockBalances.value = []
    }
  }
)

watch(
  () => form.value.type,
  async (type) => {
    if (type === 'Subtraction') {
      form.value.quantity = 0
      form.value.volume = ''
      form.value.expiryDate = ''
      if (form.value.selectedKey) {
        await loadStockBalancesForSelection(form.value.selectedKey)
      }
    } else {
      stockBalances.value = []
    }
  }
)

function buildAdjRecord(sel, overrides = {}) {
  const itemObj = sel.item
  const unitPrice =
    overrides.unitPrice != null
      ? Number(overrides.unitPrice)
      : computeDerivedUnitPrice(sel.unit, itemObj?.recent_unit_price, itemObj?.recent_volume)
  const volume = overrides.volume != null ? overrides.volume : Number(form.value.volume || 0)

  return {
    ...form.value,
    ...overrides,
    itemType: sel.type,
    itemId: sel.type === 'item' ? sel.id : null,
    productId: sel.type === 'product' ? sel.id : null,
    itemName: sel.itemName,
    itemUnit: sel.unit,
    current_balance: selectedItemBalance.value,
    unitPrice,
    variantDetails: String(volume ?? ''),
    type: overrides.type ?? form.value.type
  }
}

function processMinusAdj() {
  const sel = selectedItem.value
  if (!sel) return

  const rowsToAdjust = stockBalances.value.filter((row) => Number(row.adjustQty) > 0)
  if (rowsToAdjust.length === 0) {
    alert('Please enter at least one Qty to Adjust in the Current Balance table.')
    return
  }

  for (const row of rowsToAdjust) {
    if (Number(row.adjustQty) > Number(row.balance)) {
      alert(
        `Cannot adjust ${row.adjustQty}. Only ${row.balance} available for unit price ${row.unitPrice} and volume ${row.volume}.`
      )
      return
    }
  }

  if (isEditing.value) {
    if (rowsToAdjust.length !== 1) {
      alert('When editing, enter adjust quantity for one balance row only.')
      return
    }
    const row = rowsToAdjust[0]
    adjList.value[editIndex.value] = buildAdjRecord(sel, {
      type: 'Subtraction',
      quantity: Number(row.adjustQty),
      volume: row.volume,
      expiryDate: row.expiryDate ? String(row.expiryDate).slice(0, 10) : '',
      unitPrice: Number(row.unitPrice || 0)
    })
    isEditing.value = false
    editIndex.value = -1
  } else {
    for (const row of rowsToAdjust) {
      adjList.value.unshift(
        buildAdjRecord(sel, {
          type: 'Subtraction',
          quantity: Number(row.adjustQty),
          volume: row.volume,
          expiryDate: row.expiryDate ? String(row.expiryDate).slice(0, 10) : '',
          unitPrice: Number(row.unitPrice || 0)
        })
      )
    }
  }

  localSave()
  const header = { date: form.value.date, type: form.value.type }
  form.value = { ...getInitialForm(), ...header }
  stockBalances.value = []
}

// Actions
function processAdj() {
  const sel = selectedItem.value
  if (!sel) return

  if (isMinusMode.value) {
    processMinusAdj()
    return
  }

  const itemObj = sel.item
  const unitPrice = computeDerivedUnitPrice(
    sel.unit,
    itemObj?.recent_unit_price,
    itemObj?.recent_volume
  )

  const volume = Number(form.value.volume || 0)

  const record = buildAdjRecord(sel, {
    unitPrice,
    variantDetails: String(volume),
    volume
  })

  if (isEditing.value) {
    adjList.value[editIndex.value] = record
    isEditing.value = false
    editIndex.value = -1
  } else {
    adjList.value.unshift(record)
  }
  localSave()
  const header = { date: form.value.date, type: form.value.type }
  form.value = { ...getInitialForm(), ...header }
}

async function editItem(index) {
  const record = adjList.value[index]
  suppressBalanceWatch.value = true
  form.value = { ...record }
  form.value.selectedKey =
    record.itemType === 'product' ? `product:${record.productId}` : `item:${record.itemId}`
  isEditing.value = true
  editIndex.value = index

  if (record.type === 'Subtraction') {
    await loadStockBalancesForSelection(form.value.selectedKey, { presetRecord: record })
  } else {
    stockBalances.value = []
  }
  suppressBalanceWatch.value = false
}

function cancelEdit() {
  isEditing.value = false
  editIndex.value = -1
  stockBalances.value = []
  const header = { date: form.value.date, type: form.value.type }
  form.value = { ...getInitialForm(), ...header }
}

function deleteItem(index) {
  adjList.value.splice(index, 1)
  localSave()
}

async function saveToDatabase() {
  if (!adjList.value.length) return
  isSaving.value = true
  try {
    const first = adjList.value[0]
    const payload = {
      date: first.date,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      items: adjList.value.map(i => {
          const unitNormalized = String(i.itemUnit || '').toLowerCase().replace(/[^a-z0-9]/g, '')
          const isLiterOrKg = (
            unitNormalized === 'l' ||
            unitNormalized === 'liter' ||
            unitNormalized === 'liters' ||
            unitNormalized.startsWith('liter') ||
            unitNormalized === 'kg' ||
            unitNormalized === 'kilogram'
          )
          const qty = Number(i.quantity || 0)
          const vol = Number(i.volume || 0)
          const isPlus = i.type === 'Addition'
          const isMinus = i.type === 'Subtraction'
          const quantityForApi = (isMinus && isLiterOrKg && Number.isFinite(vol) && vol > 0)
            ? ((qty / vol) * 1000)
            : qty
          const unitPriceRaw = Number(i.unitPrice || 0)
          const unitPriceForApi = (isPlus && isLiterOrKg && Number.isFinite(vol) && vol > 0)
            ? ((unitPriceRaw * vol) / 1000)
            : unitPriceRaw

          return {
            itemId: i.itemId,
            productId: i.productId,
            quantity: quantityForApi,
            volume: i.volume,
            expiryDate: i.expiryDate || null,
            type: i.type,
            unitPrice: Number.isFinite(unitPriceForApi) ? unitPriceForApi : null,
            variantDetails: i.variantDetails ?? (i.volume != null ? String(i.volume) : null),
            remark: i.remark
          }
      })
    }
    const res = await apiFetch('/stock-adjustment', { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedAdjNo = responseData?.adjNo || responseData?.data?.adjNo || ''
      if (savedAdjNo) {
        adjList.value = adjList.value.map((row) => ({ ...row, adjNo: savedAdjNo }))
      }
      alert(savedAdjNo ? `Adjustment saved successfully. ADJ Number: ${savedAdjNo}` : 'Adjustment saved successfully.')

      if (confirm("Are you sure..........Print?")) {
          window.print()
      }
      adjList.value = []
      localStorage.removeItem('Mina_adj_temp')
      form.value = { ...getInitialForm() }
      // Refresh next ADJ number for the next entry.
      await fetchNextAdjNumberForDisplay()
    } else {
      const err = await res.json()
      alert('Error: ' + err.message)
    }
  } catch (err) { console.error(err) }
  finally { isSaving.value = false }
}

function localSave() { localStorage.setItem('Mina_adj_temp', JSON.stringify(adjList.value)) }
function localLoad() {
    const saved = localStorage.getItem('Mina_adj_temp')
    if (saved) adjList.value = JSON.parse(saved)
}

function getCategoryName(id) { return categories.value.find(c => c.id === id)?.name || '-' }
function getDisplayName(adj) { return `${adj.itemName} (${adj.itemUnit || '-'})` }

async function fetchNextAdjNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/stock-adjustment/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    adjNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching ADJ number for display:', e)
  }
}

onMounted(async () => {
  await loadData()
  localLoad()
  await fetchNextAdjNumberForDisplay()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
}

th, td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.7rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  font-size: 12px !important;
  color: inherit !important;
}

tbody tr:hover {
  background: #f1f5f9;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .h-full { height: auto !important; overflow: visible !important; }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    color: #000 !important;
    font-size: 8pt !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f0f0f0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.minus-balance-table-wrap {
  overflow-x: auto;
  overflow-y: visible;
}

.minus-balance-table {
  table-layout: auto;
  min-width: 100%;
}

.minus-balance-table th:last-child,
.minus-balance-table td:last-child {
  width: 7rem;
  min-width: 6.75rem;
  padding-right: 0.5rem;
}
</style>
